import React from 'react';
import { IBlockData } from '@core/typings';
import { BasicType, MERGE_FIELD_CLASS } from '@core/constants';
import {
	applyDynamicColorVarsToHtml,
	createBlock,
	getAncestorPinnedListing,
	resolveBoundListing,
	parseDynamicTextHelperExpression,
	replaceInlineColorBeforeRender,
	resolveMergeTagValue,
	resolvePreviewColor,
	t,
} from '@core/utils';
import { get, merge } from 'lodash';
import { BasicBlock } from '@core/components/BasicBlock';
import { normalizeMergeTagKey } from '@core/utils/mergeTagDictionary';
import { normalizeLinkHrefsInHtml } from '@core/utils/normalizeLinkHref';
import {
	AttachedListingData,
	getPreviewResolutionDataSource,
	resolveAttachedListingMergeTags,
	restoreAttachedListingMergeTags,
} from '../../../utils/resolveAttachedListingMergeTags';

const MERGE_TAG_PATTERN =
	/(\{\{\{\{raw\}\}\}\})?\{\{\s*([^{}]+?)\s*\}\}(\{\{\{\{\/raw\}\}\}\})?/gi;
const IF_MERGE_TAG_PATTERN =
	/\{\{#if\s+([\w.-]+)\}\}\s*\{\{([^{}]+?)\}\}\s*\{\{else\}\}([\s\S]*?)\{\{\/if\}\}/g;

function formatPhoneNumber(value: unknown) {
	const cleaned = String(value ?? '').replace(/\D/g, '');
	const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
	const intlCode = match?.[1] ? `+${match[1]} ` : '';

	if (match) {
		return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
	}

	return String(value ?? '');
}

function applyDynamicTextFormat(value: unknown, format?: string) {
	if (value === null || value === undefined || value === '') {
		return '';
	}

	const parseNumericValue = (input: unknown): number | null => {
		if (typeof input === 'number') {
			return Number.isFinite(input) ? input : null;
		}

		const normalized = String(input ?? '')
			.trim()
			.replace(/[^0-9.-]/g, '');

		if (!normalized || normalized === '-' || normalized === '.' || normalized === '-.') {
			return null;
		}

		const parsed = Number(normalized);
		return Number.isFinite(parsed) ? parsed : null;
	};

	const selectedFormat = String(format || 'string');

	switch (selectedFormat) {
		case 'number': {
			const numericValue = parseNumericValue(value);
			if (numericValue === null) return String(value);
			return Math.trunc(numericValue).toLocaleString('en-US', {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
			});
		}

		case 'decimal': {
			const numericValue = parseNumericValue(value);
			if (numericValue === null) return String(value);
			return numericValue.toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
		}

		case 'dollar': {
			const numericValue = parseNumericValue(value);
			if (numericValue === null) return String(value);
			return numericValue.toLocaleString('en-US', {
				style: 'currency',
				currency: 'USD',
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
			});
		}

		case 'dollarCents': {
			const numericValue = parseNumericValue(value);
			if (numericValue === null) return String(value);
			return numericValue.toLocaleString('en-US', {
				style: 'currency',
				currency: 'USD',
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
		}

		case 'phoneNumber':
			return formatPhoneNumber(value);

		case 'uppercase':
			return String(value).toUpperCase();

		case 'lowercase':
			return String(value).toLowerCase();

		case 'titlecase':
			return String(value)
				.toLowerCase()
				.split(' ')
				.map(part => part.charAt(0).toUpperCase() + part.slice(1))
				.join(' ');

		default:
			return String(value);
	}
}

function normalizeDynamicTextMaxLength(value: unknown): number | null {
	if (value === null || value === undefined || value === '') return null;

	const parsed = Number(value);
	if (!Number.isFinite(parsed)) return null;

	const normalized = Math.floor(parsed);
	return normalized > 0 ? normalized : null;
}

function truncateDynamicTextValue(value: unknown, maxLength: number | null): string {
	const content = String(value ?? '');
	if (!maxLength || maxLength < 1) return content;

	// Avoid truncating HTML fragments to prevent broken tags in rich text content.
	if (/<\/?[a-z][\s\S]*>/i.test(content)) {
		return content;
	}

	return content.length > maxLength ? content.slice(0, maxLength) : content;
}

function resolveDataSourceMergeTags(
	template: string | undefined,
	dataSource?: Record<string, any>,
	format?: string,
	maxLength?: number | null,
) {
	if (!template || !dataSource) return template || '';

	const getFormattedResolvedValue = (
		resolvedValue: unknown,
		resolvedFormat?: string,
		resolvedMaxLength?: number | null,
	): string | null => {
		const normalizedMaxLength =
			resolvedMaxLength === null || resolvedMaxLength === undefined
				? maxLength || null
				: resolvedMaxLength;

		if (
			typeof resolvedValue === 'string' ||
			typeof resolvedValue === 'number' ||
			typeof resolvedValue === 'boolean'
		) {
			return truncateDynamicTextValue(
				applyDynamicTextFormat(resolvedValue, resolvedFormat || format),
				normalizedMaxLength,
			);
		}

		if (Array.isArray(resolvedValue)) {
			const firstValue = resolvedValue.find(
				item =>
					typeof item === 'string' ||
					typeof item === 'number' ||
					typeof item === 'boolean',
			);
			if (firstValue !== undefined) {
				return truncateDynamicTextValue(
					applyDynamicTextFormat(firstValue, resolvedFormat || format),
					normalizedMaxLength,
				);
			}
		}

		return null;
	};

	const normalizeTemplateSyntax = (value: string) =>
		String(value || '')
			.replace(/&#123;|&#x7b;|&lbrace;|&lcub;/gi, '{')
			.replace(/&#125;|&#x7d;|&rbrace;|&rcub;/gi, '}');

	const resolveMergeTagsInSelectedBranch = (branchContent: string): string =>
		normalizeTemplateSyntax(branchContent).replace(MERGE_TAG_PATTERN, match => {
			const keyMatch = match.match(/\{\{\s*([^{}]+?)\s*\}\}/);
			const rawKey = String(keyMatch?.[1] || '');
			const trimmedRawKey = rawKey.trim();

			if (
				trimmedRawKey.startsWith('#') ||
				trimmedRawKey.startsWith('/') ||
				trimmedRawKey === 'else'
			) {
				return '';
			}

			const parsedHelper = parseDynamicTextHelperExpression(trimmedRawKey);
			const pipeIdx = trimmedRawKey.indexOf('|');
			const key = parsedHelper
				? parsedHelper.key
				: pipeIdx >= 0
					? trimmedRawKey.slice(0, pipeIdx).trim()
					: trimmedRawKey;
			const fallback =
				!parsedHelper && pipeIdx >= 0 ? trimmedRawKey.slice(pipeIdx + 1).trim() : '';

			const resolvedValue = resolveMergeTagValue(dataSource, key);
			const formattedValue = getFormattedResolvedValue(
				resolvedValue,
				parsedHelper?.format || format,
				parsedHelper?.maxLength,
			);

			if (formattedValue !== null && formattedValue.trim() !== '') {
				return formattedValue;
			}
			if (fallback) return fallback;
			return match;
		});

	const resolveConditionalBlocks = (content: string): string => {
		const normalizedContent = normalizeTemplateSyntax(content);
		const tokenPattern = /\{\{\s*(#if\s+([\w.-]+)|else|\/if)\s*\}\}/g;
		let output = '';
		let cursor = 0;
		let startMatch = tokenPattern.exec(normalizedContent);

		while (startMatch) {
			const token = String(startMatch[1] || '').trim();
			if (!token.startsWith('#if ')) {
				startMatch = tokenPattern.exec(normalizedContent);
				continue;
			}

			const conditionKey = String(startMatch[2] || '').trim();
			const blockStartIndex = startMatch.index;
			const trueBranchStart = tokenPattern.lastIndex;
			let depth = 1;
			let elseMatch: RegExpExecArray | null = null;
			let endMatch: RegExpExecArray | null = null;
			let nextMatch = tokenPattern.exec(normalizedContent);

			while (nextMatch) {
				const nextToken = String(nextMatch[1] || '').trim();
				if (nextToken.startsWith('#if ')) {
					depth += 1;
				} else if (nextToken === '/if') {
					depth -= 1;
					if (depth === 0) {
						endMatch = nextMatch;
						break;
					}
				} else if (nextToken === 'else' && depth === 1) {
					elseMatch = nextMatch;
				}

				nextMatch = tokenPattern.exec(normalizedContent);
			}

			if (!endMatch) break;

			const elseStart = elseMatch?.index ?? endMatch.index;
			const fallbackStart = elseMatch
				? elseMatch.index + elseMatch[0].length
				: endMatch.index;
			const trueBranch = normalizedContent.slice(trueBranchStart, elseStart);
			const fallbackBranch = normalizedContent.slice(fallbackStart, endMatch.index);
			const resolvedValue = resolveMergeTagValue(dataSource, conditionKey);
			const formattedValue = getFormattedResolvedValue(resolvedValue, format, maxLength);
			const selectedBranch =
				formattedValue !== null && formattedValue.trim() !== '' ? trueBranch : fallbackBranch;

			output += normalizedContent.slice(cursor, blockStartIndex);
			output += resolveMergeTagsInSelectedBranch(
				resolveConditionalBlocks(selectedBranch).trim(),
			);
			cursor = endMatch.index + endMatch[0].length;
			tokenPattern.lastIndex = cursor;
			startMatch = tokenPattern.exec(normalizedContent);
		}

		return output + normalizedContent.slice(cursor);
	};

	const withConditionalsResolved = resolveConditionalBlocks(template).replace(
		IF_MERGE_TAG_PATTERN,
		(_, conditionKey, trueExpression, fallbackText) => {
			const fallback = String(fallbackText || '').trim();
			const parsedHelper = parseDynamicTextHelperExpression(
				String(trueExpression || '').trim(),
			);
			const actualKey = String(parsedHelper?.key || conditionKey || '').trim();
			if (!actualKey) return fallback;

			const resolvedValue = resolveMergeTagValue(dataSource, actualKey);
			const formattedValue = getFormattedResolvedValue(
				resolvedValue,
				parsedHelper?.format || format,
				parsedHelper?.maxLength,
			);

			if (formattedValue !== null && formattedValue.trim() !== '') {
				return formattedValue;
			}

			return fallback;
		},
	);

	return withConditionalsResolved.replace(MERGE_TAG_PATTERN, match => {
		const keyMatch = match.match(/\{\{\s*([^{}]+?)\s*\}\}/);
		const rawKey = String(keyMatch?.[1] || '');
		const trimmedRawKey = rawKey.trim();

		// Skip any orphaned Handlebars block tokens
		if (
			trimmedRawKey.startsWith('#') ||
			trimmedRawKey.startsWith('/') ||
			trimmedRawKey === 'else'
		)
			return '';

		const parsedHelper = parseDynamicTextHelperExpression(trimmedRawKey);
		const pipeIdx = trimmedRawKey.indexOf('|');
		const actualKey = parsedHelper
			? parsedHelper.key
			: pipeIdx >= 0
				? trimmedRawKey.slice(0, pipeIdx).trim()
				: trimmedRawKey;
		const fallbackText =
			!parsedHelper && pipeIdx >= 0 ? trimmedRawKey.slice(pipeIdx + 1).trim() : '';

		const resolvedValue = resolveMergeTagValue(dataSource, actualKey);
		const formattedValue = getFormattedResolvedValue(
			resolvedValue,
			parsedHelper?.format || format,
			parsedHelper?.maxLength,
		);

		// A genuinely blank resolved value (a real field that exists but is empty)
		// is not a resolved value — fall through to the tag's own fallback text,
		// or the raw tag, same as an unresolved one. Never render nothing.
		if (formattedValue !== null && formattedValue.trim() !== '') {
			return formattedValue;
		}

		if (fallbackText) return fallbackText;
		return match;
	});
}

function resolveColorTagValue(
	tagExpression: string | undefined,
	dataSource?: Record<string, any>,
) {
	const rawTag = String(tagExpression || '').trim();
	if (!rawTag || !dataSource) return null;

	const extractedKey = rawTag.match(/^\{\{\s*([^{}]+?)\s*\}\}$/)?.[1]?.trim() || rawTag;

	const normalizedRawKey = normalizeMergeTagKey(extractedKey);
	const value = get(dataSource, extractedKey) ?? get(dataSource, normalizedRawKey);

	if (typeof value === 'string' && value.trim().length > 0) {
		return value.trim();
	}

	return null;
}

function escapeHtmlAttribute(value: string) {
	return String(value || '')
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

export type IText = IBlockData<
	{
		color?: string;
		border?: string;
		'border-width'?: string;
		'border-style'?: string;
		'border-color'?: string;
		'border-top-width'?: string;
		'border-right-width'?: string;
		'border-bottom-width'?: string;
		'border-left-width'?: string;
		'font-family'?: string;
		'font-size'?: string;
		'font-style'?: string;
		'font-weight'?: string;
		'line-height'?: string;
		'letter-spacing'?: string;
		height?: string;
		'text-decoration'?: string;
		'text-transform'?: string;
		'white-space'?: string;
		align?: string;
		'container-background-color'?: string;
		width?: string;
		padding?: string;
	},
	{
		content: string;
		dynamicTextTag?: string;
		dynamicFontColorTag?: string;
		dynamicTextFormat?: string;
		dynamicTextPlaceholder?: string;
		dynamicTextMaxLength?: number | string;
		dynamicTextId?: string;
		selectedListingId?: string;
		selectedListing?: AttachedListingData | null;
	}
>;

export const Text = createBlock<IText>({
	get name() {
		return t('Text');
	},
	type: BasicType.TEXT,
	create: payload => {
		const defaultData: IText = {
			type: BasicType.TEXT,
			data: {
				value: {
					content: t('This is a text block. Click once to edit it.'),
				},
			},
			attributes: {
				padding: '0px 0px 0px 0px',
				align: 'left',
				border: 'none',
				'border-width': '0px',
				'border-style': 'solid',
				'border-color': '#000000',
				'border-top-width': '',
				'border-right-width': '',
				'border-bottom-width': '',
				'border-left-width': '',
				color: '#000000',
				'container-background-color': '#FFFFFF',
				'font-family': 'Arial, sans-serif',
				'font-size': '14px',
				'line-height': '1.7',
				'letter-spacing': '0px',
				'font-weight': '400',
				'text-decoration': 'none',
				'white-space': 'normal',
				height: 'auto',
			},
			children: [],
		};
		return merge(defaultData, payload);
	},
	validParentType: [BasicType.COLUMN, BasicType.HERO],
	render(params) {
		const { data, context, mode } = params;
		const isPreviewMode = Boolean(params.dataSource?.previewMode);
		const isDesignAdmin = Boolean(params.dataSource?.isDesignAdmin);
		// Same edit/preview/production split as SingleListing, Listing, and
		// CriteriaBlock — production (and DSA admin edit) must keep the raw
		// {{listing.x}} tags for the send-time pipeline to resolve; only the
		// canvas/preview should show a bound listing's actual values.
		let actualMode: 'edit' | 'preview' | 'production';
		if (mode === 'production') {
			actualMode = 'production';
		} else if (isPreviewMode) {
			actualMode = 'preview';
		} else {
			actualMode = 'edit';
		}
		const shouldUseMergeTags =
			actualMode === 'production' || (actualMode === 'edit' && isDesignAdmin);
		const borderWidth = data.attributes['border-width'];
		const borderStyle = data.attributes['border-style'];
		// A dynamic border colour is stored as a merge tag (e.g. "{{user.brandPrimaryColor}}").
		// Resolve it to the real hex on the canvas/preview (testing mode); production keeps
		// the tag for send-time. Mirrors the Image block — and prevents a raw "{{…}}" from
		// landing in the wrapper's inline style, which the merge-tag pipeline would shatter.
		const borderColor = resolvePreviewColor(
			data.attributes['border-color'],
			params.mode,
			params.dataSource,
		);
		const borderTopWidth = data.attributes['border-top-width'];
		const borderRightWidth = data.attributes['border-right-width'];
		const borderBottomWidth = data.attributes['border-bottom-width'];
		const borderLeftWidth = data.attributes['border-left-width'];

		let attributes = data.attributes;

		const hasSideBorderWidths = [
			borderTopWidth,
			borderRightWidth,
			borderBottomWidth,
			borderLeftWidth,
		].some(value => value !== undefined && value !== null && value !== '');

		if (hasSideBorderWidths) {
			const normalizedBorderStyle = borderStyle || 'solid';
			const normalizedBorderColor = borderColor || '#000000';

			attributes = {
				...data.attributes,
				border: 'none',
				'border-top': `${borderTopWidth || '0px'} ${normalizedBorderStyle} ${normalizedBorderColor}`,
				'border-right': `${borderRightWidth || '0px'} ${normalizedBorderStyle} ${normalizedBorderColor}`,
				'border-bottom': `${borderBottomWidth || '0px'} ${normalizedBorderStyle} ${normalizedBorderColor}`,
				'border-left': `${borderLeftWidth || '0px'} ${normalizedBorderStyle} ${normalizedBorderColor}`,
			};
		}

		if (!hasSideBorderWidths && (borderWidth || borderStyle || borderColor)) {
			const combinedBorder = [
				borderWidth || '0px',
				borderStyle || 'solid',
				borderColor || '#000000',
			].join(' ');

			attributes = {
				...data.attributes,
				border: combinedBorder,
			};
		}

		// MJML's <mj-text> ignores `border`/`border-*` attributes, so a configured border
		// never renders. Build the equivalent CSS and apply it to a wrapper <div> around
		// the content instead (see border-style string assembled below).
		const borderRadius = data.attributes['border-radius'];
		const borderCssDeclarations: string[] = [];
		if (hasSideBorderWidths) {
			(['top', 'right', 'bottom', 'left'] as const).forEach(side => {
				const sideValue = attributes[`border-${side}` as keyof typeof attributes];
				if (sideValue) borderCssDeclarations.push(`border-${side}:${sideValue}`);
			});
		} else if (borderWidth || borderStyle || borderColor) {
			borderCssDeclarations.push(`border:${attributes.border}`);
		}
		if (borderCssDeclarations.length && borderRadius) {
			borderCssDeclarations.push(`border-radius:${borderRadius}`);
		}
		const borderWrapStyle = borderCssDeclarations.join(';');

		// An enclosing Listing Wrapper / listing column WINS. Everything inside a
		// listing scope shows that scope's listing, full stop — a block's own
		// binding only applies when it sits outside one. Reading the block first
		// let a child keep a listing its parent had already replaced.
		const selectedListing =
			getAncestorPinnedListing(params.context, params.idx) ||
			resolveBoundListing(data.data.value);
		const dynamicTextExpression = String(data.data.value.dynamicTextTag || '').trim();
		const dynamicFontColorTag = String(data.data.value.dynamicFontColorTag || '').trim();
		const currentTextColor = String(attributes.color || '').trim();
		const isMergeTagColor = /^\{\{\s*[^{}]+\s*\}\}$/.test(currentTextColor);
		const isMissingTextColor = !currentTextColor || isMergeTagColor;
		const dynamicTextFormat = String(
			data.data.value.dynamicTextFormat || 'string',
		).trim();
		const dynamicTextTag =
			dynamicTextExpression.match(/^\{\{\s*([^{}]+?)\s*\}\}$/)?.[1]?.trim() ||
			dynamicTextExpression;
		const dynamicTextPlaceholder = data.data.value.dynamicTextPlaceholder;
		const dynamicTextMaxLength =
			dynamicTextFormat === 'string'
				? normalizeDynamicTextMaxLength(data.data.value.dynamicTextMaxLength)
				: null;
		const dynamicTextId = (data.data.value.dynamicTextId || '').trim();
		const dynamicMarkerId =
			dynamicTextId ||
			(dynamicTextExpression && params.idx
				? `txt_dyn_${String(params.idx).replace(/[^a-zA-Z0-9_-]/g, '_')}`
				: '');

		const hasMergeTagPattern = (value: string) =>
			/\{\{\s*[^{}]+\s*\}\}/.test(String(value || ''));

		const defaultFallbackText = String(
			data.data.value.content || dynamicTextPlaceholder || '',
		);
		const fallbackSourceText = defaultFallbackText || dynamicTextExpression || '';
		// On the canvas the listing tag is left as a token so it still becomes a
		// chip and the badge resolves it for display — the same way user.* tags
		// have always behaved. Substituting the value in here replaced the chip
		// with bare text, so a bound listing field lost its underline while the
		// user field beside it kept one.
		const isRasterMode = Boolean(params.dataSource?.rasterMode);
		const isEditorCanvas = mode === 'testing' && !isPreviewMode;
		// The raster resolves the listing HERE rather than leaving the token for the
		// context object, because a design can bind a different listing to each
		// block and source.context is one flat object — it cannot hold two. Each
		// resolved value is wrapped in the merge-field span so it still reads as
		// dynamic. user.* tags are left alone; those ARE global, so they stay tokens
		// and the raster service fills them from the context.
		const resolvedFallbackContent = truncateDynamicTextValue(
			isRasterMode
				? resolveAttachedListingMergeTags(fallbackSourceText, {
						listing: selectedListing,
						wrapResolved: value => `<span class="${MERGE_FIELD_CLASS}">${value}</span>`,
					})
				: shouldUseMergeTags
					// A prior canvas render may have already locked a resolved inline
					// token into storage (see resolveAttachedListingMergeTags) — restore
					// it to a bare tag so production still sends something resolvable.
					? restoreAttachedListingMergeTags(fallbackSourceText)
					: isEditorCanvas
						? fallbackSourceText
						: resolveAttachedListingMergeTags(fallbackSourceText, {
								listing: selectedListing,
								lockResolved: true,
							}),
			dynamicTextMaxLength,
		);

		let resolvedContentForRender =
			mode === 'testing' &&
			dynamicTextExpression &&
			!hasMergeTagPattern(resolvedFallbackContent)
				? applyDynamicTextFormat(resolvedFallbackContent, dynamicTextFormat)
				: resolvedFallbackContent;

		if (mode === 'testing' && isPreviewMode && dynamicTextExpression) {
			const previewResolvedContent = resolveDataSourceMergeTags(
				dynamicTextExpression,
				getPreviewResolutionDataSource(params.dataSource, selectedListing),
				dynamicTextFormat,
				dynamicTextMaxLength,
			);
			const hasResolvedPreviewValue =
				!hasMergeTagPattern(previewResolvedContent) &&
				String(previewResolvedContent).trim().length > 0;

			if (hasResolvedPreviewValue || !resolvedContentForRender) {
				resolvedContentForRender = previewResolvedContent;
			}
		}

		// NOT in rasterMode. Nothing in the raster's dataSource can answer a user.*
		// tag (the listing was already resolved above, and user data reaches the
		// raster through source.context), so all this call could do there is
		// substitute the tag's send-time FALLBACK — bare text, no token left for
		// bay-ui to wrap and nothing for the raster service to fill. That is how
		// Design Manager ended up printing "Friend" where the field name belongs.
		// Leaving the token alone degrades to the field name instead.
		if (
			mode === 'testing' &&
			isPreviewMode &&
			!isRasterMode &&
			hasMergeTagPattern(resolvedContentForRender)
		) {
			resolvedContentForRender = resolveDataSourceMergeTags(
				resolvedContentForRender,
				getPreviewResolutionDataSource(params.dataSource, selectedListing),
			);
		}

		const resolvedDynamicFontColor =
			isPreviewMode && dynamicFontColorTag
				? resolveColorTagValue(dynamicFontColorTag, params.dataSource) ||
					dynamicFontColorTag
				: '';
		const editModePlaceholderColor =
			!isPreviewMode && dynamicFontColorTag && isMissingTextColor
				? resolveColorTagValue(dynamicFontColorTag, params.dataSource) || ''
				: '';

		if (resolvedDynamicFontColor || editModePlaceholderColor) {
			attributes = {
				...attributes,
				color: resolvedDynamicFontColor || editModePlaceholderColor,
			};
		}
		// Only strip default-black inline colors when the page theme color is a non-black
		// value — this allows the theme color to cascade.  When the page color is black
		// (the default) we keep explicit blacks so they survive email forwarding (forwards
		// strip <head> styles and break CSS inheritance chains).
		const pageTextColor = (context as any)?.data?.value?.['text-color'] as
			| string
			| undefined;
		// RTE per-selection brand colours: re-derive the inline colour from the stored
		// merge-tag key every render — testing resolves the CURRENT brand hex (so brand
		// changes show on the canvas/preview), production injects `{{key}}` for send-time
		// resolution. The hex saved in the model is only a stale snapshot.
		const contentForMode = applyDynamicColorVarsToHtml(
			resolvedContentForRender,
			mode,
			params.dataSource,
		);
		let content = normalizeLinkHrefsInHtml(
			replaceInlineColorBeforeRender(contentForMode, pageTextColor),
		);

		if (mode === 'production' && dynamicTextExpression && dynamicMarkerId) {
			content = `<span id="${dynamicMarkerId}" data-dynamic-tag="${escapeHtmlAttribute(dynamicTextTag)}">${content}</span>`;
		} else if (dynamicTextExpression) {
			// A resolved dynamic field is not free text — the value belongs to the
			// bound listing, so typing over it would silently desync the two. The
			// agent swaps, rebinds, or deletes the field instead.
			//
			// `contenteditable="false"` nested inside the block's editable region is
			// the browser's own way to carve out a read-only island; no keyboard
			// interception needed, and selection/caret behaviour stays native.
			content = `<span data-dynamic-tag="${escapeHtmlAttribute(dynamicTextTag)}" data-dynamic-locked="true" contenteditable="false">${content}</span>`;
		}

		content = content.replace(
			/^(?:<(div|span)\s+style=["']\s*white-space\s*:\s*(?:normal|nowrap)\s*;?\s*["']>([\s\S]*)<\/\1>)+$/i,
			(_, __, inner) => inner,
		);
		if (attributes['white-space'] === 'nowrap') {
			content = `<div style="white-space:nowrap;">${content}</div>`;
		}

		// Apply the border via a wrapper div, since <mj-text> drops border attributes.
		// The editor syncs the rendered contenteditable HTML back into the stored content
		// on blur, so this wrapper gets re-read on the next render. Strip any previous
		// border wrapper first (matched by its marker class) before adding the current
		// one — otherwise each border edit nests another <div> and borders stack up.
		content = content.replace(
			/^<div class="bay-text-border-wrap"[^>]*>([\s\S]*)<\/div>$/i,
			(_, inner) => inner,
		);
		if (borderWrapStyle) {
			content = `<div class="bay-text-border-wrap" style="${borderWrapStyle}">${content}</div>`;
		}

		return (
			<BasicBlock
				params={{
					...params,
					data: {
						...data,
						attributes,
					},
				}}
				tag='mj-text'
			>
				{content}
			</BasicBlock>
		);
	},
});
